// @ts-strict-ignore
import { useQueries } from '@tanstack/react-query';
import type { WatchlistResponse } from '../types';
import apiCalls from '~/helpers/apiCalls';
import { initialWatchlistData } from '../constants';
import { mergeAiRecommendations, normalizeMatches } from '../helpers';
import { useMemo } from 'react';

const { watchlists: { getMatches, postAIWatchlistResolver } } = apiCalls;

type GetMatches = {
  evaluations: Array<{
    evaluationToken: string;
    entityToken: string;
  }>;
  showLatestDecisions: boolean;
  typhonDomain: string;
};

export const useGetWatchlistMatches = ({
  evaluations,
  typhonDomain,
  showLatestDecisions = false,
}: GetMatches) => useQueries({
  queries: evaluations.map(({ entityToken, evaluationToken }) => ({
    queryKey: ['watchlist-review', entityToken, evaluationToken],
    initialData: initialWatchlistData,
    enabled: Boolean(entityToken && evaluationToken && typhonDomain),
    queryFn: async (): Promise<WatchlistResponse> => {
      const [watchlistResponse, aiResponse] = await Promise.all([
        getMatches({
          canResolve: showLatestDecisions,
          entityToken,
          evaluationToken,
          typhonDomain,
        }),
        postAIWatchlistResolver({ typhonDomain, evaluationToken, regenerate: false })
          .catch(() => null),
      ]);

      const formattedMatches = normalizeMatches(watchlistResponse);
      const matchesWithAiRecommendations = mergeAiRecommendations(formattedMatches, aiResponse);
      return {
        ...watchlistResponse,
        evaluationToken,
        matches: matchesWithAiRecommendations,
      };
    },
  })),
});

export const useGetWatchlistResolutionState = watchlistQueriesArray => useMemo(() => (
  watchlistQueriesArray.reduce((acc, { data: { matches, evaluationToken }, fetchStatus }) => {
    if (fetchStatus !== 'idle') acc.isLoadingWatchlistMatches = true; // wait for watchlists to load before allowing review
    matches.forEach(
      ({ is_resolvable: isResolvable, resolution }) => {
        if (isResolvable && !resolution.timestamp) {
          acc.unresolvedEvaluations[evaluationToken] = true;
          acc.hasUnresolvedWatchlistMatches = true;
        }
      },
    );

    return acc;
  }, {
    hasUnresolvedWatchlistMatches: false,
    isLoadingWatchlistMatches: false,
    unresolvedEvaluations: {},
  })
), [watchlistQueriesArray]);
